export const ACCESS_TIERS = {
  FREE: "free_core",
  CORE: "research_plus",
  COMING_SOON: "coming_soon",
  OWNER: "private_owner",
  NOT_FOUND: "not_found",
};

const FREE_ROUTES = new Set([
  "/setup",
  "/onboarding",
  "/dashboard",
  "/holdings",
  "/cashflow",
  "/watchlist",
  "/dictionary",
  "/learn/today",
  "/principles",
  "/search",
  "/charts",
  "/research",
  "/import",
  "/manual-entry",
  "/access-control",
  "/tax-awareness",
  "/audit",
]);

const CORE_ROUTES = new Set([
  "/discover",
  "/explore",
  "/screener",
  "/screener/etf",
  "/screener/thai",
  "/screener/funds",
  "/fair-value",
  "/valuation-drilldown",
  "/valuation",
  "/buy-decision",
  "/lens-comparison",
  "/lens-alerts",
  "/relationship-graph",
  "/relationships",
  "/scenario-studio",
  "/scenarios",
  "/decision-ledger",
  "/ledger",
  "/allocation-controller",
  "/allocation",
  "/analytics",
  "/analytics-v2",
  "/risk",
  "/financial-wellness",
  "/debt-optimizer",
  "/my-finances",
  "/health-dashboard",
  "/health",
  "/performance",
  "/benchmark",
  "/portfolio-explorer",
  "/portfolio-quality",
  "/simulate",
  "/explorer",
  "/portfolio-intelligence",
  "/portfolio-digest",
  "/backtest",
  "/whatif",
  "/what-if",
  "/income",
  "/dividend-projector",
  "/look-through",
  "/currency-risk",
  "/international",
  "/rebalance",
  "/timeline",
  "/networth",
  "/candidates",
  "/pipeline",
  "/evidence",
  "/xray",
  "/review-queue",
  "/review",
  "/actions",
  "/holdings-intraday",
  "/sleeves",
  "/controller",
  "/thesis",
  "/pl-analytics",
  "/market-overview",
  "/macro",
  "/news",
  "/earnings",
  "/position-sizer",
  "/tax-loss",
  "/dca-scheduler",
  "/journal",
  "/compare",
  "/mutual-funds",
  "/sector-heatmap",
  "/peer-comparison",
  "/research-hub",
  "/dividends",
  "/lens-diagnostics",
  "/dictionary-review",
  "/system-logic",
  "/intelligence",
  "/account-activity",
  "/cap-analysis",
  "/queue-allocator",
  "/correlation",
  "/monte-carlo",
  "/email-reports",
  "/batch-operations",
  "/fx-exposure",
  "/treemap",
  "/holdings-compare",
  "/share",
  "/snapshot-compare",
  "/app-versions",
  "/beam",
  "/bdd",
  "/review-cadence",
  "/investor-journey",
  "/investor-score",
  "/ticker-evaluation",
  "/quiz",
  "/learn/curriculum",
  "/wealth-studio",
  "/brain",
  "/portfolio-doctor",
  "/research-copilot",
  "/strategy-labs",
  "/ai-engine",
]);

const COMING_SOON_ROUTES = new Set([
  "/ai-approval",
]);

const OWNER_ROUTES = new Set([
  "/neo-intelligence",
  "/fund-os",
  "/neo-agents",
  "/ai-clone",
  "/ai-strict-clone",
  "/ai-compare",
  "/ai-what-changed",
  "/ai-history",
  "/fund-settings",
  // Research Council: backed by the private /api/council API.
  "/council",
  "/council-brief",
  "/council-orchestrator",
]);

function cleanPath(pathname = "") {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

// Dynamic (parameterised) route families. Each prefix covers "/prefix" and
// "/prefix/...". Keep these in sync with <Route path> entries in App.js;
// config/routeInventory.test.js fails when a routed path has no tier.
const PREFIX_TIERS = [
  { prefix: "/holding", tier: ACCESS_TIERS.FREE },
  { prefix: "/cashflow", tier: ACCESS_TIERS.FREE },
  { prefix: "/charts", tier: ACCESS_TIERS.FREE },
  { prefix: "/research", tier: ACCESS_TIERS.FREE },
  // Moonshot detail is opened from Holding detail (free) as well as Discover;
  // its API (/api/moonshot-os) is not research-plus gated, so it is free.
  { prefix: "/moonshot-os", tier: ACCESS_TIERS.FREE },
];

const HOLDINGS_DETAIL_RE = /^\/holdings\/[^/]+$/;

// Exposed for the route inventory guard test only.
export const ROUTE_TIER_TABLE = {
  [ACCESS_TIERS.FREE]: FREE_ROUTES,
  [ACCESS_TIERS.CORE]: CORE_ROUTES,
  [ACCESS_TIERS.COMING_SOON]: COMING_SOON_ROUTES,
  [ACCESS_TIERS.OWNER]: OWNER_ROUTES,
};

/** Every tier that claims `pathname` (should always be exactly one for a routed path). */
export function claimingTiers(pathname) {
  const path = cleanPath(pathname);
  const tiers = new Set();
  for (const { prefix, tier } of PREFIX_TIERS) {
    if (path === prefix || path.startsWith(`${prefix}/`)) tiers.add(tier);
  }
  if (HOLDINGS_DETAIL_RE.test(path)) tiers.add(ACCESS_TIERS.FREE);
  for (const [tier, routes] of Object.entries(ROUTE_TIER_TABLE)) {
    if (routes.has(path)) tiers.add(tier);
  }
  return [...tiers];
}

export function getRouteTier(pathname) {
  const path = cleanPath(pathname);
  for (const { prefix, tier } of PREFIX_TIERS) {
    if (path === prefix || path.startsWith(`${prefix}/`)) return tier;
  }
  if (HOLDINGS_DETAIL_RE.test(path)) return ACCESS_TIERS.FREE;
  if (FREE_ROUTES.has(path)) return ACCESS_TIERS.FREE;
  if (CORE_ROUTES.has(path)) return ACCESS_TIERS.CORE;
  if (COMING_SOON_ROUTES.has(path)) return ACCESS_TIERS.COMING_SOON;
  if (OWNER_ROUTES.has(path)) return ACCESS_TIERS.OWNER;
  return ACCESS_TIERS.NOT_FOUND;
}

export function canShowNavItem(item, entitlements = {}) {
  const tier = item.tier || getRouteTier(item.path);
  if (tier === ACCESS_TIERS.FREE) return Boolean(entitlements.isResearchPlus || item.showInFreeNav !== false);
  if (tier === ACCESS_TIERS.CORE) return Boolean(entitlements.isResearchPlus || item.showWhenLocked);
  if (tier === ACCESS_TIERS.COMING_SOON) return true;
  if (tier === ACCESS_TIERS.OWNER) return Boolean(entitlements.is_owner);
  return false;
}

export function isNavLocked(item, entitlements = {}) {
  const tier = item.tier || getRouteTier(item.path);
  return tier === ACCESS_TIERS.CORE && !entitlements.isResearchPlus;
}
